import { useRef, useState, useEffect, useCallback } from "react";
import { getRoomMessagesAsync } from "../../../services/chatService";
import Toaster from "../../../components/common/Toaster";
import Message from "./Message";
import moment from "moment";
import _ from "lodash";

const MessageBody = ({ room, messages, setMessages }) => {
  const [previousMessages, setPreviousMessages] = useState([]);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [loadMore, setLoadMore] = useState(false);

  const [response, setResponse] = useState([]);
  const [chats, setChats] = useState([]);

  const messageRef = useRef();
  const observer = useRef();

  const fetchMessages = useCallback(
    async (mounted) => {
      try {
        if (room) {
          setIsLoading(true);
          const result = await getRoomMessagesAsync(room.id, page);
          if (mounted) {
            setIsLoading(false);
            setResponse(result);
          }
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
      }
    },
    [page, room]
  );

  const handleMessages = useCallback(() => {
    const { result, count, total_count } = response;
    const union = _.union(previousMessages, result);
    const data = Object.assign([], union);

    setMessages(data);
    setHasMore(count < total_count);
  }, [response, previousMessages, setMessages]);

  const handleScroll = useCallback(() => {
    if (messageRef && messageRef.current && !isScrolling) {
      const { scrollHeight, clientHeight } = messageRef.current;
      messageRef.current.scrollTo({
        left: 0,
        top: scrollHeight - clientHeight,
      });
    }
  }, [isScrolling]);

  const loaderRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setLoadMore(true);
        } else {
          setIsScrolling(false);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
    setPreviousMessages(messages);
    setIsScrolling(true);
  };

  const processMessages = (result) => {
    const data = result.map((item) => {
      const container = { ...item };

      container["formatted_date"] = moment(
        item.created_on,
        moment.ISO_8601
      ).format("YYYY-MM-DD");

      container["sent_on"] = moment(item.created_on, moment.ISO_8601).format(
        "YYYY-MM-DD HH:mm"
      );

      return container;
    });

    return _.map(_.groupBy(data, "formatted_date"), (chats, formatted_date) => {
      const items = _.sortBy(chats, "id");

      return { formatted_date, items };
    });
  };

  useEffect(() => {
    let mounted = true;

    fetchMessages(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchMessages]);

  useEffect(() => {
    handleScroll();
  }, [chats, handleScroll]);

  useEffect(() => {
    handleMessages();
  }, [handleMessages]);

  useEffect(() => {
    const data = processMessages(messages);
    const sorted = _.sortBy(data, "formatted_date");

    setChats(sorted);
  }, [messages]);

  useEffect(() => {
    setPreviousMessages([]);
    setPage(1);
  }, [room]);

  return (
    <div className="h-full overflow-hidden">
      <div className="h-full overflow-y-auto scrollbar py-4" ref={messageRef}>
        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <div ref={loaderRef}></div>
          </div>
        </div>
        {hasMore && loadMore && (
          <div className="grid grid-cols-12" onClick={() => handleLoadMore()}>
            <div className="col-span-12">
              <p className="text-sm text-blue-500 cursor-pointer text-center">
                Load More
              </p>
            </div>
          </div>
        )}
        {isLoading && (
          <div className="grid grid-cols-12">
            <div className="col-span-12">
              <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse cursor-pointer text-center">
                Loading
              </p>
            </div>
          </div>
        )}
        {chats.length > 0 &&
          chats.map((item, index) => {
            return (
              <div key={index} className="grid grid-cols-12">
                <div className="col-span-12">
                  <div className="flex justify-center">
                    <div className="text-gray-400 text-sm py-2">
                      {moment(item.formatted_date).format("LL")}
                    </div>
                  </div>
                </div>
                <div className="col-span-12">
                  <div className="grid grid-cols-12 gap-y-2">
                    {item.items.map((message, index, data) => {
                      return (
                        <Message
                          key={index}
                          item={message}
                          index={index}
                          data={data}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default MessageBody;
